// src/features/audittrail/ProcessDsarDialog.tsx
// Complete or reject a DSAR — completed requests carry a response data URL,
// rejections require a justification.

import { useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { audittrailStore } from '@core/stores';
import { Button, Input, Textarea, ErrorAlert } from '@react/components';
import type { DataSubjectAccessRequest } from '@core/types/audittrail';
import { AudittrailModal } from './Modal';
import { errMessage } from './logic';

interface ProcessDsarDialogProps {
  open: boolean;
  request: DataSubjectAccessRequest | null;
  outcome: 'DSAR_STATUS_COMPLETED' | 'DSAR_STATUS_REJECTED' | null;
  onClose: () => void;
  onProcessed: () => void;
}

export const ProcessDsarDialog = observer(({ open, request, outcome, onClose, onProcessed }: ProcessDsarDialogProps) => {
  const { t } = useI18n();
  const [responseUrl, setResponseUrl] = useState('');
  const [justification, setJustification] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const isReject = outcome === 'DSAR_STATUS_REJECTED';

  const submit = async () => {
    if (!request?.entity?.id || !outcome) return;
    if (isReject && !justification.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await audittrailStore.ProcessDataSubjectAccessRequest(request.entity.id, {
        request: request.entity,
        status: outcome,
        response_data_url: !isReject ? (responseUrl.trim() || undefined) : undefined,
        justification: isReject ? justification.trim() : undefined,
      });
      setResponseUrl(''); setJustification('');
      onProcessed();
      onClose();
    } catch (e) {
      setError(errMessage(e, t('audittrail.dsar.process.error')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AudittrailModal
      open={open}
      title={isReject ? t('audittrail.dsar.process.rejectTitle') : t('audittrail.dsar.process.completeTitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={submit} isLoading={saving} disabled={isReject && !justification.trim()}>{t('audittrail.dsar.process.submit')}</Button>
        </>
      }
    >
      <div className="space-y-3">
        {error && <ErrorAlert error={error} />}
        {isReject ? (
          <Textarea label={t('audittrail.dsar.process.justification')} value={justification} onChange={(e) => setJustification(e.target.value)} rows={3} required />
        ) : (
          <Input label={t('audittrail.dsar.process.responseUrl')} value={responseUrl} onChange={(e) => setResponseUrl(e.target.value)} />
        )}
      </div>
    </AudittrailModal>
  );
});

export default ProcessDsarDialog;
